import React, { useState, useEffect } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useLocation } from 'react-router-dom';
import CompaniesList from '@/components/companies/CompaniesList';
import CompanyDetailView from '@/components/companies/CompanyDetailView';
import { fetchCompaniesWithDetails } from '@/utils/companiesHelper';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { 
  setSelectedCategories, 
  setSelectedEmployees, 
  setSelectedProductCount, 
  setSelectedModalities,
  setSelectedDiscounts 
} from '@/store/companiesFilterSlice';

const Companies = () => {
  const companies = useAppSelector(state => state.supaCompanies.companies);
  const [viewMode, setViewMode] = useState<'list' | 'details'>('list');
  const [displayMode, setDisplayMode] = useState<'cards' | 'table'>('table');
  const [selectedCompanySlug, setSelectedCompanySlug] = useState<string | null>(null);
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const location = useLocation();
  
  // Get filter parameters from URL query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoriesParam = searchParams.get('categories');
    const employeesParam = searchParams.get('employees');
    const productCountParam = searchParams.get('productCount');
    const modalitiesParam = searchParams.get('modalities');
    const discountsParam = searchParams.get('discounts');
    
    console.log('🔍 [Companies] Filter parameters from URL:', {
      categories: categoriesParam,
      employees: employeesParam,
      productCount: productCountParam,
      modalities: modalitiesParam,
      discounts: discountsParam
    });
    
    // Apply filter parameters from URL to Redux store
    if (categoriesParam) {
      const categories = categoriesParam.split(',');
      console.log('🔍 [Companies] Applying categories filter from URL:', categories);
      dispatch(setSelectedCategories(categories));
    }
    
    if (employeesParam) {
      const employees = employeesParam.split(',');
      console.log('🔍 [Companies] Applying employees filter from URL:', employees);
      dispatch(setSelectedEmployees(employees));
    }
    
    if (productCountParam) {
      const productCount = productCountParam.split(',');
      console.log('🔍 [Companies] Applying product count filter from URL:', productCount);
      dispatch(setSelectedProductCount(productCount));
    }
    
    if (modalitiesParam) {
      const modalities = modalitiesParam.split(',');
      console.log('🔍 [Companies] Applying modalities filter from URL:', modalities);
      dispatch(setSelectedModalities(modalities));
    }
    
    if (discountsParam) {
      const discounts = discountsParam.split(',');
      console.log('🔍 [Companies] Applying discounts filter from URL:', discounts);
      dispatch(setSelectedDiscounts(discounts));
    }
  }, [location.search, dispatch]);


  // Get current filter state from Redux
  const selectedCategories = useAppSelector(state => state.companiesFilter.selectedCategories) || [];
  const selectedEmployees = useAppSelector(state => state.companiesFilter.selectedEmployees) || [];
  const selectedProductCount = useAppSelector(state => state.companiesFilter.selectedProductCount) || [];
  const selectedModalities = useAppSelector(state => state.companiesFilter.selectedModalities) || [];
  const selectedDiscounts = useAppSelector(state => state.companiesFilter.selectedDiscounts) || [];
  
  // Check if any filters are active
  const hasActiveFilters = 
    (selectedCategories.length > 0 && !selectedCategories.includes('All')) ||
    (selectedEmployees.length > 0 && !selectedEmployees.includes('All')) ||
    (selectedProductCount.length > 0 && !selectedProductCount.includes('All')) ||
    (selectedModalities.length > 0 && !selectedModalities.includes('All')) ||
    (selectedDiscounts.length > 0 && !selectedDiscounts.includes('All'));
  
  // Handle company selection
  const handleCompanySelect = (slug: string) => {
    console.log('👆 [Companies] Company selected:', slug);
    
    // Navigate to the company editor page
    const url = `/edit/${slug}`;
    
    console.log(`🔍 [Companies] Navigating to: ${url}`);
    
    // Navigate to the company editor page
    window.location.href = url;
  };

  // Handle back to list
  const handleBackToList = () => {
    setViewMode('list');
    setSelectedCompanySlug(null);
  };

  const renderContent = () => {
    // Show company details if in details view mode
    if (viewMode === 'details' && selectedCompanySlug) {
      return (
        <CompanyDetailView 
          slug={selectedCompanySlug} 
          onBackToList={handleBackToList}
        />
      );
    }

    // Otherwise show the companies list
    return (
      <div className="space-y-6">
        <CompaniesList 
          onCompanySelect={handleCompanySelect} 
          viewMode={displayMode}
          onViewModeChange={setDisplayMode}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen from-purple-50 to-blue-50">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-8">
          {/* <h1 className="text-2xl font-bold text-gray-900 mb-8">
            {viewMode === 'details' ? 'Company Details' : 'Companies'}
          </h1> */}
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Companies;